export type DecisionPromptParts = {
  notice: string[];
  slider: string;
  confidence: string;
  note: string;
};

function paragraphInners(html: string): string[] {
  return [...html.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((match) => match[1]!.trim());
}

function joinSentences(a: string | undefined, b: string | undefined): string {
  return [a, b].filter((part) => part && part.length > 0).join(" ");
}

/** The confidence and note sentences were one paragraph in earlier case snapshots. */
function splitConfidenceAndNote(text: string): { confidence: string; note: string } {
  const marker = "If you wish";
  const at = text.indexOf(marker);
  if (at <= 0) return { confidence: text, note: "" };
  return { confidence: text.slice(0, at).trim(), note: text.slice(at).trim() };
}

/**
 * Decision files are four paragraphs, in order:
 * the title, the slider sentence, the optional note, then confidence.
 * Older snapshots used a different paragraph count and are still placed.
 */
export function splitDecisionPrompt(html: string): DecisionPromptParts {
  const parts = paragraphInners(html);
  if (parts.length === 4 && !/if you wish/i.test(parts[3] ?? "")) {
    return {
      notice: [parts[0] ?? ""],
      slider: parts[1] ?? "",
      note: parts[2] ?? "",
      confidence: parts[3] ?? "",
    };
  }
  if (parts.length >= 5) {
    return {
      notice: [parts[0] ?? ""],
      slider: joinSentences(parts[1], parts[2]),
      confidence: parts[3] ?? "",
      note: parts.slice(4).join(" "),
    };
  }
  if (parts.length === 4) {
    const rest = splitConfidenceAndNote(parts[3] ?? "");
    return {
      notice: [parts[0] ?? ""],
      slider: joinSentences(parts[1], parts[2]),
      confidence: rest.confidence,
      note: rest.note,
    };
  }
  if (parts.length === 3) {
    return { notice: [parts[0] ?? ""], slider: parts[1] ?? "", confidence: parts[2] ?? "", note: "" };
  }
  return { notice: parts, slider: "", confidence: "", note: "" };
}
